import L from "leaflet";

/**
 * react-leaflet relies on Leaflet's default Icon, but Vite/webpack don't know
 * how to resolve the marker images via Leaflet's relative `iconUrl`. Re-pin
 * them to Vite-resolved asset URLs.
 */
import markerIconUrl from "leaflet/dist/images/marker-icon.png";
import markerIconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import markerShadowUrl from "leaflet/dist/images/marker-shadow.png";

let installed = false;
export function installLeafletDefaultIcon() {
  if (installed) return;
  installed = true;
  // delete the buggy `_getIconUrl` so mergeOptions takes effect
  // @ts-expect-error: private leaflet field
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconUrl: markerIconUrl,
    iconRetinaUrl: markerIconRetinaUrl,
    shadowUrl: markerShadowUrl,
  });
}

const DEPOT_COLOR = "#16a34a"; // green-600
const STOP_COLOR = "#0284c7";  // sky-600 — pending/arrived (work owed)

/** Visual variant for a stop pin based on its route_stops.status. We collapse
 *  the five DB statuses into three buckets the driver actually needs to read
 *  at a glance:
 *   - "pending"   → solid blue + number  (default — work still owed)
 *   - "arrived"   → solid blue + number, ring  (currently on-site — same icon
 *                   as pending in this MVP; arrival is fleeting and gets the
 *                   driver's attention via the Detail page anyway)
 *   - "done"      → solid green + check  (completed = a sale or follow-up was
 *                   logged; visually celebrates progress)
 *   - "skipped"   → hollow grey + X  (no-answer or skipped — visited but no
 *                   contact; deprioritized but kept on map for context) */
type StopVariant = "pending" | "done" | "skipped";

const DONE_COLOR = "#16a34a";    // green-600
const SKIPPED_COLOR = "#64748b"; // slate-500

export function depotDivIcon(label: string): L.DivIcon {
  return L.divIcon({
    className: "myroute-depot-icon",
    html: `<div style="background:${DEPOT_COLOR};color:white;border:2px solid white;border-radius:9999px;padding:4px 8px;font-size:11px;font-weight:600;box-shadow:0 1px 3px rgba(0,0,0,.3);white-space:nowrap;">${label}</div>`,
    iconSize: undefined as unknown as L.PointExpression,
    iconAnchor: [20, 12],
  });
}

export function stopNumberIcon(n: number, variant: StopVariant = "pending"): L.DivIcon {
  if (variant === "done") {
    return L.divIcon({
      className: "myroute-stop-icon myroute-stop-done",
      html: `<div style="background:${DONE_COLOR};color:white;border:2px solid white;border-radius:9999px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,.3);" title="Stop ${n} — done">&#10003;</div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }
  if (variant === "skipped") {
    return L.divIcon({
      className: "myroute-stop-icon myroute-stop-skipped",
      html: `<div style="background:white;color:${SKIPPED_COLOR};border:2px solid ${SKIPPED_COLOR};border-radius:9999px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,.2);opacity:.85;" title="Stop ${n} — skipped">${n}</div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }
  return L.divIcon({
    className: "myroute-stop-icon",
    html: `<div style="background:${STOP_COLOR};color:white;border:2px solid white;border-radius:9999px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,.3);">${n}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}
